/**
 * /hypnose/ericksonienne
 * Mot-clé cible : "hypnose ericksonienne Pantin", "hypnothérapeute Pantin".
 */
import React from "react";
import { Seo } from "../../components/Layout";
import { PageHero, ProseSection, BulletList, EndCTA } from "../../components/PageParts";
import { Link } from "react-router-dom";

const INDICATIONS = [
  "Gestion du stress et de l'anxiété au quotidien",
  "Amélioration du sommeil et de l'endormissement",
  "Confiance en soi, estime, préparation d'un examen ou d'une prise de parole",
  "Blocages émotionnels liés à un événement passé",
  "Gestion des émotions et régulation du stress professionnel",
  "Accompagnement d'un changement de vie (professionnel, personnel)",
];

export default function Ericksonienne() {
  const BREADCRUMB = [
    { to: "/", label: "Accueil" },
    { to: "/hypnose", label: "L'Hypnose" },
    { label: "Hypnose ericksonienne" },
  ];
  return (
    <>
      <Seo
        title="Hypnose ericksonienne à Pantin"
        description="Hypnose ericksonienne à Pantin (15 min de Paris) et en ligne. Une méthode douce et brève pour agir sur le stress, le sommeil, la confiance en soi. Ouverture des séances en septembre 2026."
        canonical="https://www.parfaitsimparfaits.fr/hypnose/ericksonienne"
      />

      <PageHero
        breadcrumb={BREADCRUMB}
        eyebrow="Approche"
        title="Hypnose ericksonienne"
        italic="à Pantin & en ligne"
        subtitle="Une méthode moderne, brève et scientifiquement documentée pour dénouer ce qui bloque et retrouver un mieux-être durable."
      />

      {/* Bloc statut de formation — transparence sur la certification en cours */}
      <section className="px-6 lg:px-10 pb-4" data-testid="ericksonienne-formation-note">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl border border-[#B07A45]/25 bg-[#B07A45]/5 px-6 py-6 sm:px-8 sm:py-7">
            <p className="eyebrow !text-[#8A5F32] mb-3">Note importante</p>
            <div className="text-sand-800 leading-relaxed text-[15px] sm:text-base space-y-3">
              <p>
                J'accorde une grande importance à la qualité de l'accompagnement
                que je propose. C'est pourquoi je poursuis aujourd'hui mon parcours
                de formation en <strong className="text-sand-900 font-medium">hypnose ericksonienne</strong>,
                afin d'enrichir ma pratique et de disposer d'outils toujours plus
                adaptés aux besoins de chaque personne. Mon souhait est de vous
                proposer un accompagnement personnalisé, dans le respect de votre
                histoire, de votre rythme et de vos objectifs.
              </p>
              <p className="text-sand-900 font-medium">
                L'hypnose ericksonienne viendra enrichir cet accompagnement dès
                <em className="italic"> septembre 2026</em>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ProseSection>
        <h2 className="font-serif text-3xl text-sand-900 !mt-0">D'où vient l'hypnose ericksonienne&nbsp;?</h2>
        <p>
          Développée dans les années 1950 par le psychiatre américain <strong className="text-sand-900 font-medium">Milton
          H. Erickson</strong>, cette approche a profondément renouvelé la manière dont les thérapeutes
          utilisent l'hypnose. Là où l'hypnose classique cherchait à imposer, l'hypnose ericksonienne fait
          confiance aux ressources déjà présentes en chacun·e et utilise un langage indirect, imagé, personnalisé.
        </p>
        <p>
          C'est aujourd'hui l'une des formes d'hypnothérapie les plus étudiées et pratiquées en France,
          couramment intégrée à l'accompagnement du stress, des addictions, des troubles du sommeil ou
          de la confiance en soi.
        </p>

        <h2 className="font-serif text-3xl text-sand-900">Pour qui, pour quoi&nbsp;?</h2>
        <p>
          L'hypnose ericksonienne est particulièrement indiquée lorsqu'un <strong className="text-sand-900 font-medium">objectif
          précis</strong> est identifié. Voici des situations où elle donne d'excellents résultats&nbsp;:
        </p>
        <BulletList items={INDICATIONS} testid="ericksonienne-indications" />
        <p>
          Elle ne se substitue pas à un suivi médical ou psychologique en cours&nbsp;; elle s'inscrit
          en complément, comme un accompagnement de mieux-être.
        </p>

        <h2 className="font-serif text-3xl text-sand-900">Le déroulement d'une séance</h2>
        <p>
          La séance débute par un temps d'échange (~20 min) pour clarifier ensemble votre objectif et le
          cadre du travail. Vient ensuite la phase d'hypnose proprement dite, dans laquelle je vous guide
          par la voix vers un état de relaxation profonde. Vous restez conscient·e à chaque instant, libre
          de bouger, parler, ouvrir les yeux.
        </p>
        <p>
          Nous concluons par un temps d'intégration et d'échange sur ce qui a été vécu. Il faut compter
          <strong className="text-sand-900 font-medium"> environ 1h à 1h15 </strong>
          au total. Souvent, <strong className="text-sand-900 font-medium">2 à 5 séances</strong> suffisent
          pour observer un changement significatif&nbsp;— parfois moins, selon la nature du travail.
        </p>

        <h2 className="font-serif text-3xl text-sand-900">Tarif et modalités</h2>
        <p>
          <strong className="text-sand-900 font-medium">80 € la séance</strong>, à Pantin
          (Métro Église de Pantin, ligne 5, 15 min de Paris) ou en ligne, en visio. Le règlement se fait
          en fin de séance en présentiel (espèces ou chèque) ou à la réservation en ligne (virement, PayPal).
        </p>

        <p className="!mt-10">
          <Link to="/hypnose/spirituelle" className="text-[#B07A45] underline hover:text-sand-900">
            Découvrir l'hypnose spirituelle
          </Link>
          {" · "}
          <Link to="/votre-seance/deroulement" className="text-[#B07A45] underline hover:text-sand-900">
            Détails du déroulement
          </Link>
          {" · "}
          <Link to="/hypnose/questions-frequentes" className="text-[#B07A45] underline hover:text-sand-900">
            Questions fréquentes
          </Link>
        </p>
      </ProseSection>

      <EndCTA
        title="Envie d'essayer&nbsp;?"
        text="Je vous accompagne pas à pas, dans un cadre chaleureux et professionnel. Prise de rendez-vous en 1 minute."
        testid="erick-end-cta"
      />
    </>
  );
}
